import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, alpha, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Snackbar, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import { Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { deleteMyWorkFilter, getMyWork, saveMyWorkFilter, toggleMyWorkBookmark } from 'api/features';
import { GREEN, MONO, SEV_COLOR } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const STATUS_COLOR = { pending: '#f59e0b', running: '#60a5fa', paused: '#a855f7', completed: GREEN, failed: '#ef4444', cancelled: '#64748b', open: '#60a5fa', in_progress: '#f59e0b', blocked: '#ef4444', resolved: GREEN, closed: '#64748b' };
const SHOWN = 12;

const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};
const words = (s) => String(s || '').replace(/_/g, ' ');

function Mark({ on, onClick }) {
  return (
    <Tooltip title={on ? 'Remove from watchlist' : 'Add to watchlist'}>
      <IconButton size="small" aria-label={on ? 'Remove from watchlist' : 'Add to watchlist'} onClick={onClick}>
        {on ? <BookmarkIcon fontSize="small" sx={{ color: '#eab308' }} /> : <BookmarkBorderIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function Panel({ title, right, children, flex = '1 1 360px' }) {
  return (
    <Box sx={{ ...panel, flex, overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.2 }}>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{right}</Typography>
      </Stack>
      {children}
    </Box>
  );
}
const Empty = ({ children }) => <Typography color="text.secondary" variant="body2" sx={{ px: 2.8, py: 2.4, borderTop: 1, borderColor: 'divider' }}>{children}</Typography>;
const Row = ({ children }) => <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ px: 2.8, py: 1.3, borderTop: 1, borderColor: 'divider' }}>{children}</Stack>;

export default function MyWorkPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [query, setQuery] = useState('');
  const [severity, setSeverity] = useState('all');
  const [status, setStatus] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [filterName, setFilterName] = useState('');

  const say = (severityLevel, message) => setToast({ open: true, severity: severityLevel, message });
  const load = useCallback(async () => {
    try { setData(await getMyWork()); } catch (e) { say('error', e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const stats = data?.stats || {};
  const cases = data?.cases || [];
  const watchlist = data?.watchlist || [];
  const filters = data?.saved_filters || [];
  const activity = data?.recent_activity || [];
  const q = query.trim().toLowerCase();
  const hit = (...vals) => !q || vals.some((v) => String(v || '').toLowerCase().includes(q));
  const findings = useMemo(() => (data?.assigned_findings || []).filter((f) => hit(f.title, f.target, f.tool) && (severity === 'all' || f.severity === severity)), [data, q, severity]); // eslint-disable-line react-hooks/exhaustive-deps
  const scans = useMemo(() => (data?.recent_scans || []).filter((s) => hit(s.scan_type, s.target, s.phase) && (status === 'all' || s.status === status)), [data, q, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const clear = () => { setQuery(''); setSeverity('all'); setStatus('all'); };

  const mark = async (type, item) => {
    try {
      await toggleMyWorkBookmark({ resource_type: type, resource_id: item.id, label: item.title || item.scan_type || item.target || `${type} ${item.id}`, metadata: { target: item.target || '', status: item.status || '', severity: item.severity || '' } });
      await load();
    } catch (e) { say('error', e.message); }
  };
  const saveFilter = async () => {
    try { await saveMyWorkFilter({ name: filterName || 'My work filter', query: { query, severity, status } }); setDialog(false); setFilterName(''); say('success', 'Filter saved.'); await load(); } catch (e) { say('error', e.message); }
  };
  const applyFilter = (f) => { const v = f.query || {}; setQuery(v.query || ''); setSeverity(v.severity || 'all'); setStatus(v.status || 'all'); };
  const removeFilter = async (f) => { try { await deleteMyWorkFilter(f.id); await load(); } catch (e) { say('error', e.message); } };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  const shown = showAll ? findings : findings.slice(0, SHOWN);
  const stat = (label, value, note, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 180px' }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 30, fontWeight: 800, fontFamily: MONO, lineHeight: 1.2, color }}>{value || 0}</Typography>
      <Typography variant="caption" color="text.secondary">{note}</Typography>
    </Box>
  );

  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>My work</Typography>
        <Typography color="text.secondary" variant="body2">What is open for you: problems to fix, your cases and scans, and what you are watching</Typography>
      </Box>

      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Open problems', stats.total_findings, `${stats.high || 0} high`, undefined)}
        {stat('Critical', stats.critical, 'fix these first', stats.critical ? SEV_COLOR.critical : undefined)}
        {stat('Cases', stats.cases_active, 'open or in progress', undefined)}
        {stat('Scans running', stats.running_scans, 'right now', '#60a5fa')}
      </Stack>

      <Box sx={{ ...panel, p: 2 }}>
        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Search problems, websites, scans" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ flex: '1 1 280px' }} inputProps={{ 'aria-label': 'Search' }} />
          <TextField select size="small" label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)} sx={{ minWidth: 150 }}>
            {['all', 'critical', 'high', 'medium', 'low', 'info'].map((s) => <MenuItem key={s} value={s}>{s === 'all' ? 'All severities' : s}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Scan status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
            {['all', 'pending', 'running', 'paused', 'completed', 'failed', 'cancelled'].map((s) => <MenuItem key={s} value={s}>{s === 'all' ? 'All statuses' : s}</MenuItem>)}
          </TextField>
          <Button onClick={() => setDialog(true)} sx={{ textTransform: 'none', color: GREEN, fontWeight: 700 }}>Save this filter</Button>
          <Button color="inherit" onClick={clear} sx={{ textTransform: 'none' }}>Clear</Button>
        </Stack>
        {filters.length > 0 && (
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
            {filters.map((f) => <Chip key={f.id} label={f.name} onClick={() => applyFilter(f)} onDelete={() => removeFilter(f)} sx={{ bgcolor: alpha(GREEN, 0.12), fontWeight: 700 }} />)}
          </Stack>
        )}
      </Box>

      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
        <Panel title="Problems to fix" right={`${findings.length} open`} flex="2 1 600px">
          {!findings.length && <Empty>{data?.assigned_findings?.length ? 'Nothing matches this filter.' : 'Nothing is open. Well done.'}</Empty>}
          {shown.map((f) => (
            <Row key={f.id}>
              <Box sx={{ flex: '2 1 240px', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{f.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{f.target}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ width: 64 }}>{timeAgo(f.created_at)}</Typography>
              <Pill color={SEV_COLOR[f.severity] || '#64748b'}>{String(f.severity || 'info').toUpperCase()}</Pill>
              <Typography sx={{ width: 36, textAlign: 'right', fontFamily: MONO, fontWeight: 800, color: '#fcd34d' }}>{Math.round(f.risk_score || 0)}</Typography>
              <Mark on={f.bookmarked} onClick={() => mark('finding', f)} />
              <Button size="small" component={RouterLink} to="/vulns" sx={{ textTransform: 'none', color: GREEN, fontWeight: 700 }}>Open</Button>
            </Row>
          ))}
          {findings.length > SHOWN && (
            <Box sx={{ p: 1.5, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
              <Button size="small" color="inherit" onClick={() => setShowAll((v) => !v)} sx={{ textTransform: 'none' }}>{showAll ? 'Show fewer' : `Show all ${findings.length}`}</Button>
            </Box>
          )}
        </Panel>

        <Box sx={{ flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Panel title="My cases" right={`${cases.length}`} flex="1 1 auto">
            {!cases.length && <Empty>No active cases.</Empty>}
            {cases.slice(0, 7).map((c) => (
              <Row key={c.id}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{c.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.target || 'No website'} · {words(c.status)}</Typography>
                </Box>
                <Pill color={SEV_COLOR[c.priority] || '#64748b'}>{String(c.priority || '').toUpperCase()}</Pill>
                <Mark on={c.bookmarked} onClick={() => mark('case', c)} />
              </Row>
            ))}
            {cases.length > 0 && <Box sx={{ p: 1.2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}><Button size="small" component={RouterLink} to="/cases" color="inherit" sx={{ textTransform: 'none' }}>Open cases</Button></Box>}
          </Panel>
          <Panel title="Watching" right={`${watchlist.length}`} flex="1 1 auto">
            {!watchlist.length && <Empty>Use the bookmark on a problem, case or scan to keep it here.</Empty>}
            {watchlist.slice(0, 8).map((w) => (
              <Row key={w.id}>
                <BookmarkIcon fontSize="small" sx={{ color: '#eab308' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{w.label || `${w.resource_type} ${w.resource_id}`}</Typography>
                  <Typography variant="caption" color="text.secondary">{w.resource_type} · saved {timeAgo(w.created_at)}</Typography>
                </Box>
              </Row>
            ))}
          </Panel>
        </Box>
      </Stack>

      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
        <Panel title="My scans" right={`${scans.length}`} flex="2 1 600px">
          {!scans.length && <Empty>No scans match this filter.</Empty>}
          {scans.slice(0, 12).map((s) => {
            const c = STATUS_COLOR[s.status] || '#64748b';
            return (
              <Row key={s.id}>
                <Box sx={{ flex: '2 1 220px', minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{words(s.scan_type)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{s.target}</Typography>
                </Box>
                <Pill color={c}>{String(s.status || '').toUpperCase()}</Pill>
                <Typography variant="caption" color="text.secondary" sx={{ width: 70 }}>{s.findings} found</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 64 }}>{timeAgo(s.created_at)}</Typography>
                <Mark on={s.bookmarked} onClick={() => mark('scan', s)} />
                <Button size="small" component={RouterLink} to="/jobs" sx={{ textTransform: 'none', color: GREEN, fontWeight: 700 }}>Jobs</Button>
              </Row>
            );
          })}
        </Panel>
        <Panel title="Recent activity" right="" flex="1 1 340px">
          {!activity.length && <Empty>No recent activity.</Empty>}
          {activity.map((a) => (
            <Row key={a.id}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14, textTransform: 'capitalize' }}>{words(a.action)}</Typography>
                <Typography variant="caption" color="text.secondary">{a.detail || a.resource_type} · {timeAgo(a.time)}</Typography>
              </Box>
            </Row>
          ))}
        </Panel>
      </Stack>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save this filter</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Name" value={filterName} onChange={(e) => setFilterName(e.target.value)} sx={{ mt: 1 }} placeholder="Critical on the shop" />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Keeps the search, severity and scan status you have set now.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveFilter} sx={{ bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: GREEN } }}>Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Stack>
  );
}
